import React, { useEffect, useState } from 'react';
import { Globe, Loader2, CheckCircle2, XCircle, ChevronDown, ChevronUp, PlugZap } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { competitorsAPI } from '../services/api';

const PROVIDERS = [
  { id: 'none', label: 'Aucun service' },
  { id: 'scrapingbee', label: 'ScrapingBee (proxy premium FR + rendu JS)' },
  { id: 'scraperapi', label: 'ScraperAPI (proxy premium FR + rendu JS)' },
];
const VIA_LABEL = { direct: 'Connexion directe', proxy: 'Proxy', scrapingbee: 'ScrapingBee', 'scrapingbee stealth': 'ScrapingBee (mode furtif)', scraperapi: 'ScraperAPI', 'scraperapi stealth': 'ScraperAPI (ultra premium)' };

const TestResults = ({ test }) => (
  <div className="mt-3 rounded-md border bg-gray-50 p-3 text-xs" data-testid="competitors-conn-test-results">
    <p className="text-gray-500 mb-2 truncate">Test sur : {test.url}</p>
    <ul className="space-y-1">
      {test.results.map(r => (
        <li key={r.via} className="flex items-center gap-2" data-testid={`competitors-conn-test-${r.via}`}>
          {r.ok ? <CheckCircle2 size={14} className="text-green-600" /> : <XCircle size={14} className="text-red-500" />}
          <span className="font-medium">{VIA_LABEL[r.via] || r.via}</span>
          <span className="text-gray-500">{r.ok ? `page reçue (${Math.round(r.chars / 1000)} ko)${r.price != null ? ` · prix détecté ${r.price} €` : ' · prix non détecté dans les données structurées'}` : r.error}</span>
        </li>
      ))}
    </ul>
  </div>
);

const CompetitorConnectionSettings = ({ settings, onSaved, defaultTestUrl }) => {
  const [open, setOpen] = useState(false);
  const [proxy, setProxy] = useState(settings?.proxy_url || '');
  const [provider, setProvider] = useState(settings?.scraper_provider || 'none');
  const [apiKey, setApiKey] = useState('');
  const [testUrl, setTestUrl] = useState(defaultTestUrl || '');
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [test, setTest] = useState(null);
  useEffect(() => { setProxy(settings?.proxy_url || ''); setProvider(settings?.scraper_provider || 'none'); }, [settings?.proxy_url, settings?.scraper_provider]);
  const [usage, setUsage] = useState(null);
  useEffect(() => { if (settings?.scraper_provider === 'scrapingbee' && settings?.scraper_api_key_set) competitorsAPI.scraperUsage().then(r => setUsage(r.usage)).catch(() => {}); }, [settings?.scraper_provider, settings?.scraper_api_key_set]);
  if (!settings) return null;

  const active = !!settings.proxy_url || (settings.scraper_provider !== 'none' && settings.scraper_api_key_set);

  const save = async () => {
    setSaving(true);
    try {
      const payload = { proxy_url: proxy, scraper_provider: provider };
      if (apiKey.trim()) payload.scraper_api_key = apiKey.trim();
      if (provider === 'none') payload.scraper_api_key = '';
      const r = await competitorsAPI.setConnection(payload);
      onSaved(r); setApiKey('');
      toast.success('Connexion sortante enregistrée');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Enregistrement impossible');
    } finally {
      setSaving(false);
    }
  };

  const runTest = async () => {
    if (!testUrl.trim()) return toast.error('Indiquez une URL concurrente à tester');
    setTesting(true); setTest(null);
    try {
      setTest(await competitorsAPI.testConnection(testUrl.trim()));
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Test impossible');
    } finally {
      setTesting(false);
    }
  };

  return (
    <div className="mt-3 rounded-lg border bg-white" data-testid="competitors-connection-settings">
      <button type="button" onClick={() => setOpen(o => !o)} className="w-full flex items-center justify-between px-4 py-2.5 text-sm" data-testid="competitors-conn-toggle">
        <span className="inline-flex items-center gap-2 font-medium text-gray-800">
          <Globe size={15} className="text-[#1e3a5f]" /> Connexion sortante (contournement HTTP 403)
          <span className={`text-[11px] px-2 py-0.5 rounded-full ${active ? 'bg-green-100 text-green-700' : 'bg-amber-100 text-amber-700'}`} data-testid="competitors-conn-status">
            {active ? `Actif · ${[settings.proxy_url && 'proxy', settings.scraper_provider !== 'none' && settings.scraper_api_key_set && VIA_LABEL[settings.scraper_provider]].filter(Boolean).join(' + ')}` : 'Direct uniquement'}
          </span>
        </span>
        {open ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
      </button>
      {open && (
        <div className="px-4 pb-4 border-t pt-3 space-y-3 text-sm">
          <p className="text-xs text-gray-500">Les sites concurrents (Oscaro, Autodoc, Mister-Auto…) bloquent les IP de serveurs étrangers. Si le relevé direct échoue (403), il est retenté via le proxy puis via le service de scraping configuré ci-dessous, avec une IP française.</p>
          <div className="grid gap-3 md:grid-cols-2">
            <label className="block">
              <span className="text-xs font-medium text-gray-600">Proxy HTTP(S) français</span>
              <Input value={proxy} onChange={e => setProxy(e.target.value)} placeholder="http://utilisateur:motdepasse@hote:port" data-testid="competitors-conn-proxy" className="mt-1 h-9 text-sm" />
            </label>
            <label className="block">
              <span className="text-xs font-medium text-gray-600">Service de scraping</span>
              <select value={provider} onChange={e => setProvider(e.target.value)} data-testid="competitors-conn-provider" className="mt-1 w-full h-9 border rounded-md px-2 text-sm bg-white">
                {PROVIDERS.map(p => <option key={p.id} value={p.id}>{p.label}</option>)}
              </select>
            </label>
            {provider !== 'none' && (
              <label className="block md:col-span-2">
                <span className="text-xs font-medium text-gray-600">Clé API {VIA_LABEL[provider]} {settings.scraper_api_key_set && <span className="text-gray-400">(clé enregistrée {settings.scraper_api_key_hint} — laisser vide pour conserver)</span>}</span>
                {usage && (
                  <span className={`ml-2 inline-flex items-center rounded-full px-2 py-0.5 text-[11px] font-medium ${usage.exhausted ? 'bg-red-100 text-red-700' : 'bg-green-100 text-green-800'}`} data-testid="scraper-usage-badge">
                    {usage.exhausted ? `Quota épuisé : ${usage.used}/${usage.max} crédits — renouvellement le ${usage.renewal}. Augmentez votre plan sur scrapingbee.com ou attendez.` : `${usage.used}/${usage.max} crédits utilisés ce mois (renouvellement ${usage.renewal})`}
                  </span>
                )}
                <Input type="password" value={apiKey} onChange={e => setApiKey(e.target.value)} placeholder={settings.scraper_api_key_set ? '••••••••' : 'Collez votre clé API'} data-testid="competitors-conn-apikey" className="mt-1 h-9 text-sm" />
                <span className="text-[11px] text-gray-400">Obtenez une clé sur {provider === 'scrapingbee' ? 'app.scrapingbee.com' : 'scraperapi.com'} (≈ 1000 requêtes gratuites/mois).</span>
              </label>
            )}
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <Button size="sm" onClick={save} disabled={saving} className="bg-[#1e3a5f] hover:bg-[#1e3a5f]/90" data-testid="competitors-conn-save">
              {saving ? <Loader2 size={14} className="mr-1 animate-spin" /> : null} Enregistrer
            </Button>
            <div className="flex-1 min-w-[240px] flex items-center gap-2">
              <Input value={testUrl} onChange={e => setTestUrl(e.target.value)} placeholder="https://www.oscaro.com/… (URL à tester)" data-testid="competitors-conn-test-url" className="h-9 text-sm" />
              <Button size="sm" variant="outline" onClick={runTest} disabled={testing} data-testid="competitors-conn-test">
                {testing ? <Loader2 size={14} className="mr-1 animate-spin" /> : <PlugZap size={14} className="mr-1" />} Tester
              </Button>
            </div>
          </div>
          {test && <TestResults test={test} />}
        </div>
      )}
    </div>
  );
};

export default CompetitorConnectionSettings;
